import { useEffect } from "react";
import { Message } from "@/types";
import { ChatNavState } from "./useProjectBoundaryReset";
import { ChatStreamingRefs } from "./useChatStreamingRefs";

interface UseNewProjectInitOptions {
  projectId: string | undefined;
  navStateRef: React.MutableRefObject<ChatNavState>;
  messageCount: number;
  currentConversationId: string | null;
  createConversation: (projectId?: string) => string;
  addMessage: (conversationId: string, message: Omit<Message, "id">) => string;
  refs: ChatStreamingRefs;
}

/**
 * When the user arrives from HomePage with `isNewProject=true`, this effect:
 *   1. Copies the sessionId from navigation state into a ref so it persists
 *      across re-renders for subsequent `handleSendMessage` calls.
 *   2. Adds the user's prompt as a chat message and creates an empty AI
 *      placeholder for streaming. The WebSocket ping was already sent by
 *      HomePage — this just sets up the UI to receive responses.
 */
export function useNewProjectInit({
  projectId,
  navStateRef,
  messageCount,
  currentConversationId,
  createConversation,
  addMessage,
  refs,
}: UseNewProjectInitOptions): void {
  // Effect: Session ID initialization
  // Copies the sessionId from navigation state into a ref so it persists across
  // re-renders and is available for subsequent handleSendMessage calls.
  // Only relevant when arriving from HomePage with a new project.
  useEffect(() => {
    if (navStateRef.current?.sessionId) {
      refs.sessionIdRef.current = navStateRef.current.sessionId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Effect: New project setup from homepage
  // When the user arrives from HomePage with isNewProject=true, adds their prompt
  // as a user message and creates an empty AI placeholder for streaming.
  useEffect(() => {
    // Safety guard when a new project starts
    if (
      !projectId ||
      !navStateRef.current?.isNewProject ||
      !navStateRef.current?.description ||
      messageCount !== 0
    ) {
      return;
    }

    let conversationId = currentConversationId;
    if (!conversationId) {
      // Pass projectId so the conversation is properly associated with this project.
      // This is critical for getConversationsByProjectId to find it later
      // like when re-opening the project from the projects page.
      conversationId = createConversation(projectId);
    }

    // Add the user message, carrying any attachments sent with the first
    // prompt so they render as badges in the history.
    const initialAttachments = navStateRef.current.attachments;
    addMessage(conversationId, {
      content: navStateRef.current.description,
      isUser: true,
      ...(initialAttachments && initialAttachments.length > 0
        ? { attachments: initialAttachments }
        : {}),
    });

    // Add a placeholder AI message for streaming
    const aiMessageId = addMessage(conversationId, {
      content: "",
      isUser: false,
      isLoading: true,
    });
    refs.streamingMessageIdRef.current = aiMessageId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectId,
    messageCount,
    addMessage,
    createConversation,
    currentConversationId,
  ]);
}
